import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// TH-3b — the Camera Director. `follow` (default off) eases the camera to frame each
// landed exchange cluster and lights it (ring + full opacity) while the rest of the
// DAG dims — the "performing canvas." Twin of TheaterModeContext. spotlightTurnKeys is
// the SINGLE source consumed by both the dim (useDagNodeOpacity) and the ring.
export const DIRECTOR_FOLLOW_PREF_KEY = 'directorFollowPref';

type CameraDirectorValue = {
  follow: boolean;
  paused: boolean;
  toggleFollow: () => void;
  setFollow: (on: boolean) => void;
  spotlightTurnKeys: ReadonlySet<string> | null;
  setSpotlightTurnKeys: (keys: ReadonlySet<string> | null) => void;
  setPaused: (p: boolean) => void;
};

export const CameraDirectorContext = createContext<CameraDirectorValue>({
  follow: false, paused: false, toggleFollow: () => {}, setFollow: () => {},
  spotlightTurnKeys: null, setSpotlightTurnKeys: () => {}, setPaused: () => {},
});

const isTypingTarget = (el: EventTarget | null): boolean => {
  const n = el as HTMLElement | null;
  if (!n) return false;
  const tag = n.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || n.isContentEditable === true;
};

export const CameraDirectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [follow, setFollowState] = useState(false);
  const [paused, setPaused] = useState(false);
  const [spotlightTurnKeys, setSpotlightTurnKeys] = useState<ReadonlySet<string> | null>(null);

  const setFollow = useCallback((on: boolean) => {
    setFollowState(on);
    try { localStorage.setItem(DIRECTOR_FOLLOW_PREF_KEY, on ? '1' : '0'); } catch { /* tolerate */ }
    if (!on) { setSpotlightTurnKeys(null); setPaused(false); }   // D6 — off clears the lighting
  }, []);
  const toggleFollow = useCallback(() => setFollow(!follow), [follow, setFollow]);

  useEffect(() => {   // restore persisted pref once
    try { setFollowState(localStorage.getItem(DIRECTOR_FOLLOW_PREF_KEY) === '1'); } catch { /* default off */ }
  }, []);

  useEffect(() => {   // ONE global hotkey — `f` toggles, guarded while typing
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;   // don't eat Cmd/Ctrl+F (find-in-page)
      if (e.key === 'f' || e.key === 'F') { e.preventDefault(); setFollow(!follow); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [follow, setFollow]);

  // every DAG node subscribes to this ctx (useDagNodeOpacity) — memoize so an
  // App-level re-render doesn't churn the whole canvas.
  const value = useMemo(() => ({
    follow, paused, toggleFollow, setFollow, spotlightTurnKeys, setSpotlightTurnKeys, setPaused,
  }), [follow, paused, toggleFollow, setFollow, spotlightTurnKeys]);

  return (
    <CameraDirectorContext.Provider value={value}>
      {children}
    </CameraDirectorContext.Provider>
  );
};

export const useCameraDirector = () => useContext(CameraDirectorContext);
